// Importing required modules from the 'three' library and custom components
import * as THREE from 'three';
import Camera from './components/Camera';
import EventDispatcher from './common/EventDispatcher';
import AssetLoader from './components/AssetLoader';
import GameScene from './components/GameScene';
import { Global } from './common/global';
import GameControl from './components/GameControl';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import * as CANNON from 'cannon';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { Server } from './common/callServer';
import Confetti from './components/Confetti';

import './styles/main.scss';
import './styles/privacy.scss';
import './styles/age.scss';
import './styles/avatar.scss';
import './styles/footer.scss';
import './styles/register.scss';
import './styles/code.scss';
import './styles/start.scss';
import './styles/game.scss';
import './styles/game.scss';
import './styles/score.scss';
import './styles/leader.scss';
import './styles/intro.scss';

import { isMobile, isMobileOnly } from 'mobile-device-detect';

import Sounds from './components/Sounds';
import Shooter from './components/Shooter';
import Ball from './components/Ball';
import Keeper from './components/Keeper';
import UI from './components/UI/UI';
import gsap from 'gsap';
import Lights from './components/Lights';

let lastTime = 0;
let fps = 0;
let frameCount = 0;
let lastDeltaVec = new THREE.Vector3();

export default class Game extends THREE.Scene {
  environmentMap = null;
  sizes = {
    width: window.innerWidth,
    height: window.innerHeight,
  };

  constructor() {
    super();
    window.addEventListener('resize', this.onResize.bind(this));
  }
  init() {
    this.canRender = false;
    lastTime = 0;
    fps = 0;
    frameCount = 0;
    // this.stats = new Stats();
    // this.stats.showPanel(0);
    // document.body.appendChild(this.stats.dom);
    this.assetsLoaded = false;
    Global.gameTry = 1;
    Global.userRegistered = false;
    Global.controlType = null;
    this.fixedTimeStep = 1 / 60; // Fixed time step for physics (60 FPS)
    this.accumulator = 0; // Accumulated time since the last physics step

    this.physicsTimeScale = isMobile ? 0.95 : 1;
    this.gameTime = 0;
    this.events = EventDispatcher.getObj();
    this.events.addEventListener('LOADER_EVENTS', this.onEvents.bind(this));
    this.events.addEventListener('GAME_EVENTS', this.onEvents.bind(this));
    this.events.addEventListener('GAMESCENE_EVENTS', this.onEvents.bind(this));
    this.events.addEventListener('PLAYER_EVENTS', this.onEvents.bind(this));

    this.canvas = document.querySelector('canvas.webgl');
    this.clock = new THREE.Clock();

    this.camera = new Camera(
      60,
      this.sizes.width / this.sizes.height,
      0.1,
      200
    );
    this.camera.init();
    this.add(this.camera);

    this.createLevel();

    this.ui = new UI();
    this.ui.init();

    this.gameSounds = new Sounds(this, this.camera);
    this.gameSounds.init();

    this.gameControl = new GameControl(this);
    this.gameControl.init();

    // this.confetti = new Confetti(this, {
    //   count: 2000,
    //   origin: new THREE.Vector3(-1, 6.5, -55),
    // });
    // this.confetti.burst(200);

    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      precision: 'highp',
      powerPreference: 'high-performance',
    });

    this.renderer.physicallyCorrectLights = true;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMappingExposure = 1;
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.gammaFactor = 2.2;
    this.renderer.gammaOutput = false;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.outputEncoding = THREE.sRGBEncoding;
    this.renderer.setSize(this.sizes.width * 1, this.sizes.height * 1);
    this.renderer.setPixelRatio(
      Math.min(Math.max(2, window.devicePixelRatio), isMobile ? 1.5 : 1.5)
    );

    this.assetLoader = new AssetLoader(this);
    this.assetLoader.init();

    // this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    // this.controls.enableDamping = true;
    // this.controls.dampingFactor = 0.05;
    // this.controls.rotateSpeed = -0.8;
    // this.controls.enabled = true;
    // this.controls.minDistance = 1; // Minimum distance the camera can zoom in
    // this.controls.maxDistance = 30; // Maximum distance the camera can zoom out

    // this.controls.target.set(0, -3, 5);

    this.onResize();
    this.tick();

    const rgbeLoader = new RGBELoader();
    rgbeLoader.load('./textures/environmentMaps/1/bg.hdr', (environmentMap) => {
      environmentMap.mapping = THREE.EquirectangularReflectionMapping;
      Global.envMap = environmentMap;

      this.assetLoader.prepare();
    });

    // this.animateButtons();
  }
  animateButtons() {
    this.frameDate = {
      value: 1,
    };
    this.lastVal = 0;
    gsap.to(this.frameDate, {
      value: 31, // Animates from 10 to 0
      duration: 1.5, // 10 seconds
      ease: 'none',
      repeat: -1,
      delay: 1,
      onUpdate: function () {
        if (this.lastVal != Math.floor(this.frameDate.value)) {
          document
            .querySelector('#ageYes')
            .classList.remove(`sprite_white${this.lastVal}`);
          document
            .querySelector('#ageNo')
            .classList.remove(`sprite_white${this.lastVal}`);
          document
            .querySelector('#ageConfirm')
            .classList.remove(`sprite_white${this.lastVal}`);
          document
            .querySelector('#privacy_notice #proceedBtn')
            .classList.remove(`sprite_white${this.lastVal}`);
          document
            .querySelector('#register_sec #getOTPBtn')
            .classList.remove(`sprite_white${this.lastVal}`);
          document
            .querySelector('#code_sec #codeVerifyBtn')
            .classList.remove(`sprite_white${this.lastVal}`);
          document
            .querySelector('#start_sec #startNowBtn')
            .classList.remove(`sprite_white${this.lastVal}`);
          document
            .querySelector('#start_sec #playNow')
            .classList.remove(`sprite_white${this.lastVal}`);
          document
            .querySelector('#intro_scr #letsPlay')
            .classList.remove(`sprite_white${this.lastVal}`);

          this.lastVal = Math.floor(this.frameDate.value);

          document
            .querySelector('#ageYes')
            .classList.add(`sprite_white${this.lastVal}`);
          document
            .querySelector('#ageNo')
            .classList.add(`sprite_white${this.lastVal}`);
          document
            .querySelector('#ageConfirm')
            .classList.add(`sprite_white${this.lastVal}`);
          document
            .querySelector('#privacy_notice #proceedBtn')
            .classList.add(`sprite_white${this.lastVal}`);
          document
            .querySelector('#register_sec #getOTPBtn')
            .classList.add(`sprite_white${this.lastVal}`);
          document
            .querySelector('#code_sec #codeVerifyBtn')
            .classList.add(`sprite_white${this.lastVal}`);
          document
            .querySelector('#start_sec #startNowBtn')
            .classList.add(`sprite_white${this.lastVal}`);
          document
            .querySelector('#start_sec #playNow')
            .classList.add(`sprite_white${this.lastVal}`);
          document
            .querySelector('#intro_scr #letsPlay')
            .classList.add(`sprite_white${this.lastVal}`);
        }
      }.bind(this),
    });
  }
  createLevel() {
    this.initPhysics();

    this.gameScene = new GameScene(this);
    this.gameScene.init();

    this.ball = new Ball(this);
    this.ball.init();

    this.shooter = new Shooter(this);
    this.shooter.init();

    this.keeper = new Keeper(this);
    this.keeper.init();

    this.lights = new Lights(this);
    this.lights.init();

    // this.defender1 = new Defender(this);
    // this.defender1.init('defender1');

    // this.defender2 = new Defender(this);
    // this.defender2.init('defender2');

    // this.defender3 = new Defender(this);
    // this.defender3.init('defender3');
  }

  initPhysics() {
    this.world = new CANNON.World();
    this.world.gravity.set(0, -9.82, 0);
    this.world.broadphase = new CANNON.SAPBroadphase(this.world);
    // this.world.allowSleep = true;

    Global.ballMaterial = new CANNON.Material('ballMaterial');
    Global.floorMaterial = new CANNON.Material('floorMaterial');
    Global.netMaterial = new CANNON.Material('netMaterial');
    Global.netMaterial2 = new CANNON.Material('netMaterial2');

    this.ballFloorContactMaterial = new CANNON.ContactMaterial(
      Global.ballMaterial,
      Global.floorMaterial,
      {
        friction: 1,
        restitution: 0.25,
      }
    );
    this.ballNetContactMaterial = new CANNON.ContactMaterial(
      Global.ballMaterial,
      Global.netMaterial,
      {
        friction: 0.04,
        restitution: 0.2,
      }
    );
    this.world.addContactMaterial(this.ballFloorContactMaterial);
    this.world.addContactMaterial(this.ballNetContactMaterial);
  }
  showStartCounter() {
    this.countDown = new Countdown(this, this.camera);
    this.countDown.init();
  }
  startGame() {
    Global.isGameActive = true;
  }
  hideAllScreen() {}

  onEvents(data) {
    switch (data['message']['event_type']) {
      case 'load_complete':
        if (this.assetsLoaded) return;

        // Global.gender = 'Male';
        this.assetsLoaded = true;
        document.querySelector('#splash_scr').classList.remove('active');
        this.setUpGame();
        break;
      case 'replay_game':
        this.replayGame(false);
        break;

      case 'init_scene':
        this.showScene();
        this.startGameTimer();
        this.setupFreekick();
        break;
      case 'start_game':
        this.startGame();
        this.setupFreekick();
        break;
      case 'game_over':
        this.stopGame(true);

        break;
      // case 'goal':
      case 'defend':
      case 'retry':
        this.prepareNextTry(data['message']['event_type']);
        break;
    }
  }
  showScene() {
    this.canRender = true;
    this.events.dispatchEvent({
      type: 'SOUND_EVENTS',
      message: {
        event_type: 'stop_sound',
        data: {
          sndKey: 'music',
        },
      },
    });
    gsap.delayedCall(0.1, () => {
      this.events.dispatchEvent({
        type: 'SOUND_EVENTS',
        message: {
          event_type: 'play_sound',
          data: {
            sndKey: 'crowd',
          },
        },
      });
    });
    gsap.delayedCall(0.25, () => {
      document.querySelector('#game_view').classList.add('active');
    });
  }
  async onGameFinish() {}

  startGameTimer() {
    if (Global.introShown) {
      this.events.dispatchEvent({
        type: 'GAMESCENE_EVENTS',
        message: {
          event_type: 'show_screen',
          data: {
            screenKey: 'Game',
          },
        },
      });

      this.events.dispatchEvent({
        type: 'GAMESCENE_EVENTS',
        message: {
          event_type: 'start_time',
          data: {},
        },
      });
    }
    // document.querySelector('.game_header').classList.add('active');
  }
  stopTimer() {
    this.gameTimer && clearInterval(this.gameTimer);
  }
  updateTime() {
    this.gameTime += 100;
    let totalSeconds = Math.floor(this.gameTime / 1000); // Convert to total seconds
    let minutes = Math.floor(totalSeconds / 60); // Calculate total minutes
    let seconds = totalSeconds % 60; // Calculate remaining seconds

    document.querySelector('#info_time .txt').innerHTML = `${
      minutes < 10 ? '0' : ''
    }${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }

  setUpGame() {
    this.environment = Global.envMap;
    this.background = Global.envMap;
    this.backgroundIntensity = 1;

    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'create_scene',
        data: {},
      },
    });
    this.events.dispatchEvent({
      type: 'SOUND_EVENTS',
      message: {
        event_type: 'init_sounds',
        data: {},
      },
    });

    // this.events.dispatchEvent({
    //   type: 'GAMESCENE_EVENTS',
    //   message: {
    //     event_type: 'show_screen',
    //     data: {
    //       screenKey: 'ShowAvatarSelection',
    //     },
    //   },
    // });
    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'show_screen',
        data: {
          screenKey: 'AgeVerify',
        },
      },
    });
  }

  stopGame(quickTransition) {
    Global.isGameActive = false;
    this.canRender = false;
    this.hideHeader();
    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'hide_intro',
        data: {},
      },
    });

    this.events.dispatchEvent({
      type: 'GAME_EVENTS',
      message: {
        event_type: 'dispose',
        data: {},
      },
    });
    this.dispose();

    this.events.dispatchEvent({
      type: 'SOUND_EVENTS',
      message: {
        event_type: 'stop_sound',
        data: {
          sndKey: 'crowd',
        },
      },
    });
    gsap.delayedCall(0.1, () => {
      this.events.dispatchEvent({
        type: 'SOUND_EVENTS',
        message: {
          event_type: 'play_sound',
          data: {
            sndKey: 'music',
          },
        },
      });
    });
    Global.serverObj.send(Global.URL_DATA, null, null, {
      saveType: 'score',
      score: Global.totalGoalsSaved,
      yob: Global.yob,
      gender: Global.gender,
      name: Global.userName,
      mobile: Global.mobile,
      email: Global.email,
      jerseyname: Global.avatarName,
      location: Global.location,
      IGhandle: Global.IGHandle,
      uniqID: Global.U_ID,
    });
    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'show_screen',
        data: {
          screenKey: 'Score',
        },
      },
    });
  }
  prepareNextTry(type) {
    gsap.delayedCall(type == 'retry' ? 0 : 4, this.setupFreekick.bind(this));
    // setTimeout(this.setupFreekick.bind(this), type == 'retry' ? 0 : 2000);
    // if (Global.shootCnt <= 4) {
    // } else {
    //   this.stopGame(false);
    // }
  }
  showHeader() {
    document.querySelector('#game_header').classList.add('active');

    gsap.delayedCall(0.1, () => {
      document.querySelector('#game_header').classList.add('active2');
    });
  }
  hideHeader() {
    document.querySelector('#game_header').classList.remove('active');
    document.querySelector('#game_header').classList.remove('active2');
  }
  setupFreekick() {
    if (Global.shootCnt == 0) {
      this.events.dispatchEvent({
        type: 'GAMESCENE_EVENTS',
        message: {
          event_type: 'show_screen',
          data: {
            screenKey: 'Game',
          },
        },
      });
    }

    Global.shootDirection = null;
    Global.shootCnt++;
    Global.xPadd = 1.25 * (Global.shootCnt - 1) * 2;
    Global.xPadd =
      Math.random() < 0.5
        ? Math.abs(Global.xPadd) * -1
        : Math.abs(Global.xPadd);
    Global.xPadd = 0;
    /* Math.sign(Global.xPadd) * Math.min(Math.abs(Global.xPadd), 6) */ const ballPosition =
      new THREE.Vector3(-0.2, 0.1, -15 - Math.abs(Global.xPadd * 0.75));

    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'assign_position',
        data: {
          position: ballPosition,
        },
      },
    });

    if (!Global.isGameActive) return;

    gsap.delayedCall(0.5, () => {
      this.events.dispatchEvent({
        type: 'SOUND_EVENTS',
        message: {
          event_type: 'play_sound',
          data: {
            sndKey: 'blow',
          },
        },
      });
    });
    // return;
    this.showHeader();
    gsap.delayedCall(1, () => {
      this.events.dispatchEvent({
        type: 'GAMESCENE_EVENTS',
        message: {
          event_type: 'kick_ball',
          data: {
            curve_fact: this.swipeX - this.initSwipeX, //endPoint.x-middlePoint.x,
            swipeDir: Math.sign(this.swipeX),
          },
        },
      });
    });
    // setTimeout(() => {}, 1000);
    if (Global.shootCnt == 1 && !Global.introShown) {
      this.events.dispatchEvent({
        type: 'GAMESCENE_EVENTS',
        message: {
          event_type: 'show_screen',
          data: {
            screenKey: 'Intro',
          },
        },
      });
      //start_time
    }
  }
  // Method to handle game replay
  replayGame(isGameTry = false) {
    // Global.jumpHeight = 'high';
    // this.events.dispatchEvent({
    //   type: 'GAME_EVENTS',
    //   message: {
    //     event_type: 'reset_camera',
    //     data: {},
    //   },
    // });
    // this.events.dispatchEvent({
    //   type: 'SOUND_EVENTS',
    //   message: {
    //     event_type: 'stop_sound',
    //     data: {
    //       sndKey: 'music',
    //     },
    //   },
    // });
    // this.events.dispatchEvent({
    //   type: 'SOUND_EVENTS',
    //   message: {
    //     event_type: 'play_sound',
    //     data: {
    //       sndKey: 'crowd',
    //     },
    //   },
    // });

    // document.querySelector(".game_header .time").innerHTML = '00:00:00'
    // this.initDirectionsSaved = false;
    Global.gameTry++;
    Global.shootDirection = null;
    Global.shootCnt = 0;
    this.createLevel();

    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'create_scene',
        data: {},
      },
    });
    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'init_scene',
        data: {},
      },
    });

    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'update_post_feature',
        data: {},
      },
    });
    gsap.delayedCall(1, () => {
      this.events.dispatchEvent({
        type: 'GAMESCENE_EVENTS',
        message: {
          event_type: 'start_game',
          data: {},
        },
      });
      /*  this.events.dispatchEvent({
        type: 'CAMERA_EVENTS',
        message: {
          event_type: 'add_intro_view',
          data: {},
        },
      }); */
    });

    // this.events.dispatchEvent({
    //   type: 'GAMESCENE_EVENTS',
    //   message: {
    //     event_type: 'start_game',
    //     data: {},
    //   },
    // });
    // this.onStart();
  }
  // Event handler for window resize
  onResize() {
    // Update sizes
    this.sizes.width = window.innerWidth; /* isMobile
      ? window.innerWidth
      : 0.56 * window.innerHeight; */
    this.sizes.height = window.innerHeight;

    // Dispatching events to update camera and renderer sizes
    this.events.dispatchEvent({
      type: 'CAMERA_EVENTS',
      message: {
        event_type: 'resize',
        data: {
          aspect: this.sizes.width / this.sizes.height,
          sizes: this.sizes,
        },
      },
    });
    if (
      (window.innerWidth <= window.innerHeight && isMobile) ||
      (window.innerWidth >= window.innerHeight && !isMobile)
    ) {
      // document.querySelector('#rotate').classList.remove('active');
    } else {
      // document.querySelector('#rotate').classList.add('active');
    }
    // Updating renderer size and pixel ratio
    this.renderer.setSize(this.sizes.width, this.sizes.height);
    this.renderer.setPixelRatio(
      Math.min(Math.max(2, window.devicePixelRatio), isMobile ? 1.5 : 1.5)
    );
  }

  // Update method to render the scene and dispatch update events
  update(now, delta) {
    // if (!Global.isGameActive) return;
    this.renderer.render(this, this.camera);
    this.events.dispatchEvent({
      type: 'GAMESCENE_EVENTS',
      message: {
        event_type: 'update',
        data: {
          delta: delta,
          camera: this.camera,
          now: now,
        },
      },
    });
  }

  tick(now) {
    if (!this.canRender) {
      window.requestAnimationFrame(this.tick.bind(this));
      return;
    }
    this.stats && this.stats.begin();
    this.controls && this.controls.update();

    const delta = (now - lastTime) / 1000; // Convert milliseconds to seconds
    lastTime = now;

    frameCount++;
    if (delta) {
      fps = frameCount / delta;
      frameCount = 0;
    } else {
    }

    let ticks = Math.ceil(delta / (1 / 120));
    ticks = Math.min(ticks, 20);
    Global.fps = new THREE.Vector3(0, Global.fps, 0).lerp(
      new THREE.Vector3(0, fps, 0),
      0.1
    ).y;
    const newDelta = Math.min(
      (delta ? Math.min(delta * 100, 1) : 1) * (120 / (fps || 1)),
      10
    );
    Global.delta = THREE.MathUtils.lerp(Global.delta, newDelta, 0.01);
    const scaledDelta = delta * this.physicsTimeScale;
    this.update(now, lastDeltaVec.y);

    if (!isNaN(scaledDelta)) {
      this.accumulator += scaledDelta;
      while (this.accumulator >= this.fixedTimeStep) {
        this.ball && this.ball.updateTrail();
        this.world && this.world.step && this.world.step(this.fixedTimeStep); // Step the physics simulation
        this.accumulator -= this.fixedTimeStep; // Reduce the accumulator
      }
    }
    if (this.confetti) {
      const wind = new THREE.Vector3(
        Math.sin(now * 0.9) * 1,
        0,
        Math.cos(now * 0.6) * 0
      );
      this.confetti.update(delta, wind);
    }

    this.stats && this.stats.end();
    window.requestAnimationFrame(this.tick.bind(this));
  }
  dispose() {
    document.querySelector('#game_view').classList.remove('active');
    document.querySelector('#game_view').classList.remove('active2');

    if (this.world) {
      while (this.world.bodies.length > 0) {
        this.world.removeBody(this.world.bodies[0]);
      }
      // Optionally remove constraints if used
      // while (this.world.constraints.length > 0) {
      //   this.world.removeConstraint(this.world.constraints[0]);
      // }
      this.world = null;
    }

    this.ballFloorContactMaterial = null;
    this.ballNetContactMaterial = null;

    // Clear references to materials
    Global.ballMaterial = null;
    Global.floorMaterial = null;
    Global.netMaterial = null;
    Global.netMaterial2 = null;
  }
}

window.onload = async function () {
  //   const result = await createUser();
  //   if (!localStorage.getItem('masterKey')) {
  //     localStorage.setItem('masterKey', result.userKey);
  //   }
  //   localStorage.setItem('dataKey', result.dataKey);
  //   const states = await getStates();
  //   document.querySelector(
  //     '#state'
  //   ).innerHTML += `<option value="">Select</option>`;
  //   states.forEach((state) => {
  //     document.querySelector(
  //       '#state'
  //     ).innerHTML += `<option value='${state.stateName}'>${state.stateKey}</option>`;
  //   });
  checkDevice();

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const source = urlParams.get('utm_source');
  const medium = urlParams.get('utm_medium');
  const content = urlParams.get('utm_content');
  const term = urlParams.get('utm_term');

  Global.serverObj = new Server();
  Global.serverObj.send(
    Global.URL_CREATE,
    setUID.bind(this),
    null,
    {
      device: isMobile ? 'mobile' : 'web',
      fresh: true,
      source,
      medium,
      content,
      term,
    },
    'POST',
    null,
    false
  );
  const game = new Game();
  game.init();
};

function setUID(v) {
  Global.U_ID = JSON.parse(v)['UID'];
  Global.gameKey = JSON.parse(v)['gamekey'];

  Global.serverObj.setDetails(Global.U_ID, Global.gameKey);
}

function checkDevice() {
  // document.querySelector('#comingsoon').classList.add('active');
  if (!isMobile) {
    // document.querySelector('#intro_scr').classList.add('desktop');
    document.querySelector('.continue_scr').classList.add('desktop');
    document.querySelector('#game_header').classList.add('desktop');
    document.querySelector('#rotate').classList.add('desktop');
    document.querySelector('#age_screen').classList.add('desktop');
    document.querySelector('#not_eligible').classList.add('desktop');
    document.querySelector('#register_screen').classList.add('desktop');
    document.querySelector('#avatar_screen').classList.add('desktop');
    document.querySelector('#score_screen').classList.add('desktop');
    document.querySelector('#leaderboard').classList.add('desktop');
  }
}
